import { FeatureResponse } from "@9amhealth/openapi";
import FunnelKey from "src/constants/funnelKey";
import { FUNNEL_QUESTIONNAIRE_ELIGIBILITY } from "src/constants/misc";
import { SignupCustomCampaignName } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import FactorySignupCustomCampaign from "src/ui/components/SignupCustomContent/campaigns/FactorySignupCustomCampaign";
import { SignupCustomCampaignCDCDPP } from "./SignupCustomCampaignCDCDPP";
import { CalendarDateTime } from "@internationalized/date";

enum EligibilityQuestionId {
  employer = "nnr4MisyYRqS",
  healthPlan = "Xr1URwgDrLB8"
}

enum EligibilityEmployerChoiceId {
  airLife = "d5LScrPCWIn5",
  albar = "1fc4hYxx53KH",
  amazon = "ALXY6RYKBVTr",
  americanCollegeOfRheumatology = "OO0vMMAJzWLt",
  apple = "AjiuetUcuy5D",
  countyOfDoor = "54wElWwh6SQs",
  demo = "TvMRqMrvfkMi",
  dexterRXISchoolDistrict = "aCYQiWFp54IV",
  effectiveSchool = "PtfGzLTRxA6c",
  eptam = "wPYZDAGHVnYs",
  exceptionalMotorcars = "wbcgBT1kCY5x",
  firstChoice = "1nJ65cyhh18G",
  fortBendCountyGovernment = "5GRgfu0B9f4J",
  fresenius = "3b1Bli2b6BZe",
  generalPlastics = "XXhxi0BA1XZO",
  horizonHealthPartners = "djD0aDXW3ISk",
  hydacTechnologyCorporation = "KAPqVRi51kMD",
  jimSchmidtChevrolet = "TDyAAMxBXc5v",
  jjSafety = "YWlwgEuAOeI7",
  johnsonAndJordanInc = "rYJFS43iWn5c",
  jpns = "qLo0sGkxDUBT",
  kineticGroup = "KS7beV6g2Ll7",
  knipper = "uZJi37MuHVp6",
  kwestGroup = "g7zSaWeQqDFv",
  kwikTrip = "oZlpyMeJMxwX",
  lilly = "vVjcv17F6WlJ",
  lochmuellerGroup = "sQav0cgzyXLh",
  managementMcoa = "MZOHxK2xDhXt",
  markCubanCompaniesMcchp = "NXWjVvLRh64U",
  matrixMedical = "plS9FZzLY2jT",
  bjs = "F8GN2UPKvArt",
  nimnicht = "KdoCwOdxKMsp",
  norAmColdStorage = "4fK82L5MDc8Z",
  northernPartnersCooperative = "ovG8A1ZTFviK",
  northfieldHospitalAndClinics = "GsDzrbqqF4Zs",
  permianRegionalHospital = "polqMy0A0OhF",
  prescryptive = "xBdIqSpWXKNF",
  revisionEnergy = "Yr9LKhj9TGWI",
  searsSeating = "5k2YF8z8T8m2",
  seveeAndMaherEngineers = "yOblIQrWH9PP",
  silverstoneLiving = "KtuJcQBWYDxx",
  sorensonCommunications = "ksfZ0BP67VOi",
  stateOfIndiana = "PxcU4opoCxXT",
  theBoelterCompanies = "xcPpJXMhJdgP",
  tkg = "eFiS5CSR8ZNW",
  unitedDerm = "o3YJ9iNpfdw9",
  unitedTrailers = "e8Y25dGSuvTw",
  weAreBlood = "2PclwiU3z3G2",
  wipfli = "hroIvEv66bHy",
  soleraPebtf = "s5o9le8r8a01",
  stateOfGeorgia = "Wv6vkFS2uyz2"
}

enum EligibilityHealthPlanChoiceId {
  anthemIndiana = "nvwPwhoPWjJv",
  evernorth = "EhJryoxsCGQQ",
  cignaIfp = "WSms00VLXTju",
  solera = "s1oDlEeVr9aQ",
  transcarent = "CAEVcKrBxBvN"
}

const eligibilityEmployerChoiceValues = {
  [EligibilityEmployerChoiceId.airLife]: "AirLife",
  [EligibilityEmployerChoiceId.albar]: "Albar",
  [EligibilityEmployerChoiceId.amazon]: "Amazon",
  [EligibilityEmployerChoiceId.americanCollegeOfRheumatology]:
    "American College of Rheumatology",
  [EligibilityEmployerChoiceId.apple]: "Apple",
  [EligibilityEmployerChoiceId.countyOfDoor]: "County of Door",
  [EligibilityEmployerChoiceId.demo]: "Demo",
  [EligibilityEmployerChoiceId.bjs]: "BJ's",
  [EligibilityEmployerChoiceId.dexterRXISchoolDistrict]:
    "Dexter R-XI School District",
  [EligibilityEmployerChoiceId.effectiveSchool]: "Effective School",
  [EligibilityEmployerChoiceId.eptam]: "EPTAM",
  [EligibilityEmployerChoiceId.exceptionalMotorcars]: "Exceptional Motorcars",
  [EligibilityEmployerChoiceId.firstChoice]: "First Choice",
  [EligibilityEmployerChoiceId.fortBendCountyGovernment]:
    "Fort Bend County Government",
  [EligibilityEmployerChoiceId.fresenius]: "Fresenius",
  [EligibilityEmployerChoiceId.generalPlastics]: "General Plastics",
  [EligibilityEmployerChoiceId.horizonHealthPartners]:
    "Horizon Health Partners",
  [EligibilityEmployerChoiceId.hydacTechnologyCorporation]:
    "Hydac Technology Corporation",
  [EligibilityEmployerChoiceId.jimSchmidtChevrolet]: "Jim Schmidt Chevrolet",
  [EligibilityEmployerChoiceId.jjSafety]: "JJ Safety",
  [EligibilityEmployerChoiceId.johnsonAndJordanInc]: "Johnson & Jordan Inc",
  [EligibilityEmployerChoiceId.jpns]: "JPNS",
  [EligibilityEmployerChoiceId.kineticGroup]: "Kinetic Group",
  [EligibilityEmployerChoiceId.knipper]: "Knipper",
  [EligibilityEmployerChoiceId.kwestGroup]: "KWest Group",
  [EligibilityEmployerChoiceId.kwikTrip]: "Kwik Trip",
  [EligibilityEmployerChoiceId.lilly]: "Lilly",
  [EligibilityEmployerChoiceId.lochmuellerGroup]: "Lochmueller Group, Inc.",
  [EligibilityEmployerChoiceId.managementMcoa]: "Management MCOA",
  [EligibilityEmployerChoiceId.markCubanCompaniesMcchp]:
    "Mark Cuban Companies (MCHCP)",
  [EligibilityEmployerChoiceId.matrixMedical]: "Matrix Medical",
  [EligibilityEmployerChoiceId.nimnicht]: "Nimnicht",
  [EligibilityEmployerChoiceId.norAmColdStorage]: "Nor-Am Cold Storage",
  [EligibilityEmployerChoiceId.northernPartnersCooperative]:
    "Northern Partners Cooperative",
  [EligibilityEmployerChoiceId.northfieldHospitalAndClinics]:
    "Northfield Hospital and Clinics",
  [EligibilityEmployerChoiceId.permianRegionalHospital]:
    "Permian Regional Hospital (PRMC)",
  [EligibilityEmployerChoiceId.prescryptive]: "Prescryptive",
  [EligibilityEmployerChoiceId.revisionEnergy]: "Revision Energy",
  [EligibilityEmployerChoiceId.searsSeating]: "Sears Seating",
  [EligibilityEmployerChoiceId.seveeAndMaherEngineers]:
    "Sevee & Maher Engineers",
  [EligibilityEmployerChoiceId.silverstoneLiving]: "Silverstone Living",
  [EligibilityEmployerChoiceId.sorensonCommunications]:
    "Sorenson Communications, LLC",
  [EligibilityEmployerChoiceId.stateOfIndiana]: "State of Indiana",
  [EligibilityEmployerChoiceId.theBoelterCompanies]: "The Boelter Companies",
  [EligibilityEmployerChoiceId.tkg]: "TKG",
  [EligibilityEmployerChoiceId.unitedDerm]: "United Derm",
  [EligibilityEmployerChoiceId.unitedTrailers]: "United Trailers",
  [EligibilityEmployerChoiceId.weAreBlood]: "We Are Blood",
  [EligibilityEmployerChoiceId.wipfli]: "Wipfli",
  [EligibilityEmployerChoiceId.soleraPebtf]: "PEBTF",
  [EligibilityEmployerChoiceId.stateOfGeorgia]: "State of Georgia"
};

const eligibilityHealthPlanChoiceValues = {
  [EligibilityHealthPlanChoiceId.anthemIndiana]: "Anthem Indiana",
  [EligibilityHealthPlanChoiceId.evernorth]: "Evernorth",
  [EligibilityHealthPlanChoiceId.cignaIfp]: "Cigna IFP",
  [EligibilityHealthPlanChoiceId.solera]: "Solera",
  [EligibilityHealthPlanChoiceId.transcarent]: "Transcarent"
};

const campaignInstances = () => [
  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Solera,
    prettyName: "Solera",
    funnelKey: FunnelKey.solera,
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.healthPlan,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityHealthPlanChoiceId.solera,
            value:
              eligibilityHealthPlanChoiceValues[
                EligibilityHealthPlanChoiceId.solera
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.StateOfIndiana,
    prettyName: "State of Indiana",
    funnelKey: FunnelKey.stateOfIndiana,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/536581e6692a17af77dc2c15085a9e3b019c5668-3840x848.png",
    logoSize: "large",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.stateOfIndiana,
            value:
              eligibilityEmployerChoiceValues[
                EligibilityEmployerChoiceId.stateOfIndiana
              ]
          }
        },
        {
          questionId: EligibilityQuestionId.healthPlan,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityHealthPlanChoiceId.anthemIndiana,
            value:
              eligibilityHealthPlanChoiceValues[
                EligibilityHealthPlanChoiceId.anthemIndiana
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Evernorth,
    prettyName: "Evernorth",
    contactForIneligibilityFileCheck: "9am",
    funnelKey: FunnelKey.evernorth,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/eca72b7fe025eb6e20dc82459a575706d2666423-438x77.svg",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.healthPlan,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityHealthPlanChoiceId.evernorth,
            value:
              eligibilityHealthPlanChoiceValues[
                EligibilityHealthPlanChoiceId.evernorth
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.KwikTrip,
    prettyName: "Kwik Trip",
    funnelKey: FunnelKey.kwikTrip,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/cc79972cfb5a8c427e39373deae3f50d4369cd66-375x60.png",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.kwikTrip,
            value:
              eligibilityEmployerChoiceValues[
                EligibilityEmployerChoiceId.kwikTrip
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Prescryptive,
    prettyName: "Prescryptive",
    funnelKey: FunnelKey.prescryptive,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/ef478da257d2f1cbc3482d519d243617ab42e211-349x138.webp",
    filterAnswerOptions: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          includeOptions: [
            EligibilityEmployerChoiceId.firstChoice,
            EligibilityEmployerChoiceId.generalPlastics,
            EligibilityEmployerChoiceId.prescryptive
          ]
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Universal,
    prettyName: "Universal",
    funnelKey: FunnelKey.universal,
    getSubscriptionFromSuggestedTreatmentPlan: true,
    personalisedFinalSuccessScreen: true
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Lilly,
    prettyName: "Lilly",
    funnelKey: FunnelKey.lilly,
    getSubscriptionFromSuggestedTreatmentPlan: true,
    personalisedFinalSuccessScreen: true
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.SmithRx,
    prettyName: "SmithRx",
    funnelKey: FunnelKey.smithrx,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/ebe6b7808efc33fe80bb8a5259f867965cc656ca-180x34.svg",
    filterAnswerOptions: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          includeOptions: [
            EligibilityEmployerChoiceId.airLife,
            EligibilityEmployerChoiceId.albar,
            EligibilityEmployerChoiceId.americanCollegeOfRheumatology,
            EligibilityEmployerChoiceId.countyOfDoor,
            EligibilityEmployerChoiceId.dexterRXISchoolDistrict,
            EligibilityEmployerChoiceId.effectiveSchool,
            EligibilityEmployerChoiceId.eptam,
            EligibilityEmployerChoiceId.exceptionalMotorcars,
            EligibilityEmployerChoiceId.fortBendCountyGovernment,
            EligibilityEmployerChoiceId.fresenius,
            EligibilityEmployerChoiceId.horizonHealthPartners,
            EligibilityEmployerChoiceId.hydacTechnologyCorporation,
            EligibilityEmployerChoiceId.jimSchmidtChevrolet,
            EligibilityEmployerChoiceId.johnsonAndJordanInc,
            EligibilityEmployerChoiceId.jjSafety,
            EligibilityEmployerChoiceId.jpns,
            EligibilityEmployerChoiceId.kineticGroup,
            EligibilityEmployerChoiceId.knipper,
            EligibilityEmployerChoiceId.kwestGroup,
            EligibilityEmployerChoiceId.lochmuellerGroup,
            EligibilityEmployerChoiceId.managementMcoa,
            EligibilityEmployerChoiceId.markCubanCompaniesMcchp,
            EligibilityEmployerChoiceId.matrixMedical,
            EligibilityEmployerChoiceId.nimnicht,
            EligibilityEmployerChoiceId.norAmColdStorage,
            EligibilityEmployerChoiceId.northernPartnersCooperative,
            EligibilityEmployerChoiceId.northfieldHospitalAndClinics,
            EligibilityEmployerChoiceId.permianRegionalHospital,
            EligibilityEmployerChoiceId.revisionEnergy,
            EligibilityEmployerChoiceId.searsSeating,
            EligibilityEmployerChoiceId.seveeAndMaherEngineers,
            EligibilityEmployerChoiceId.silverstoneLiving,
            EligibilityEmployerChoiceId.sorensonCommunications,
            EligibilityEmployerChoiceId.theBoelterCompanies,
            EligibilityEmployerChoiceId.tkg,
            EligibilityEmployerChoiceId.unitedDerm,
            EligibilityEmployerChoiceId.unitedTrailers,
            EligibilityEmployerChoiceId.weAreBlood
          ]
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Wipfli,
    prettyName: "Wipfli",
    funnelKey: FunnelKey.wipfli,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/6d4ff0f3fc17c198204eb23366484630328d0437-924x301.png",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.wipfli,
            value:
              eligibilityEmployerChoiceValues[
                EligibilityEmployerChoiceId.wipfli
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Transcarent,
    prettyName: "Transcarent",
    funnelKey: FunnelKey.transcarent,
    availabilityChecks: {
      blockAnonymousUsers: true,
      textContext: "error"
    },
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.healthPlan,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityHealthPlanChoiceId.transcarent,
            value:
              eligibilityHealthPlanChoiceValues[
                EligibilityHealthPlanChoiceId.transcarent
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Amazon,
    prettyName: "Amazon",
    funnelKey: FunnelKey.amazon,
    availabilityChecks: {
      featureFlag: "AMAZON_WEIGHTLOSS" as FeatureResponse.feature,
      textContext: "notAvailableYet"
    },
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/93fb16874daaebdb21161feaecca6812dbf51473-739x129.svg",
    logoSize: "large",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.amazon,
            value:
              eligibilityEmployerChoiceValues[
                EligibilityEmployerChoiceId.amazon
              ]
          }
        }
      ]
    }
  }),

  new SignupCustomCampaignCDCDPP(),
  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.LillyTeam,
    prettyName: "Lilly Team",
    funnelKey: FunnelKey.lillyTeam,
    logo: "https://cdn.sanity.io/images/g38rxyoc/production/534da4ef5cfc1e325a159785ba16c741fcfc3d0a-631x344.svg",
    logoSize: "small",
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.lilly,
            value: "Lilly"
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.Demo,
    prettyName: "Demo",
    funnelKey: FunnelKey.demo,
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.demo,
            value:
              eligibilityEmployerChoiceValues[EligibilityEmployerChoiceId.demo]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.NovoCare,
    prettyName: "NovoCare",
    funnelKey: FunnelKey.novocare
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.CignaIfp,
    prettyName: "Cigna IFP",
    funnelKey: FunnelKey.cignaIfp,
    availabilityChecks: {
      releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
    },
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.healthPlan,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityHealthPlanChoiceId.cignaIfp,
            value:
              eligibilityHealthPlanChoiceValues[
                EligibilityHealthPlanChoiceId.cignaIfp
              ]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.BJs,
    prettyName: "BJ's",
    funnelKey: FunnelKey.bjs,
    contactForIneligibilityFileCheck: "employer",
    availabilityChecks: {
      releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
    },
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.bjs,
            value:
              eligibilityEmployerChoiceValues[EligibilityEmployerChoiceId.bjs]
          }
        }
      ]
    }
  }),

  new FactorySignupCustomCampaign({
    name: SignupCustomCampaignName.stateOfGeorgia,
    prettyName: "State of Georgia",
    funnelKey: FunnelKey.stateOfGeorgia,
    contactForIneligibilityFileCheck: "9am",
    availabilityChecks: {
      releaseDate: new CalendarDateTime(2026, 1, 1, 0, 0, 0)
    },
    answerOverrides: {
      [FUNNEL_QUESTIONNAIRE_ELIGIBILITY]: [
        {
          questionId: EligibilityQuestionId.employer,
          fieldType: "choice",
          fieldValue: {
            choiceId: EligibilityEmployerChoiceId.stateOfGeorgia,
            value:
              eligibilityEmployerChoiceValues[
                EligibilityEmployerChoiceId.stateOfGeorgia
              ]
          }
        }
      ]
    }
  })
];

const SignupCustomCampaignGenericInstances = campaignInstances().map((c) =>
  c.get()
);

export { campaignInstances };
export default SignupCustomCampaignGenericInstances;
